import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Container,
    Group,
    Paper,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Loader,
    Alert,
    SegmentedControl,
    Badge
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import QRGenerator from "../../../components/QRGenerator";
import { getAuthHeaders } from "../../../api/authHelper";

type PersonDto = {
    personId: number;
    firstName: string;
    lastName: string;
};

type EventDto = {
    eventID?: number;
    eventName?: string;
};

type PersonsResponse = {
    items?: PersonDto[];
    Items?: PersonDto[];
};

type AssignedParkingDto = {
    parkingPersonId: number;
    eventId: number;
    personId: number;
    firstName?: string;
    lastName?: string;
    note?: string;
    isActive?: boolean;
    createdAt?: string;
};

type EventQrCreateResponse = {
    parkingQrCodeId?: number;
    token?: string;
    validDate?: string;
};

type ParkingQrListDto = {
    parkingQrCodeId: number;
    eventId: number;
    eventName?: string;
    token: string;
    validDate: string;
    isUsed: boolean;
    usedAt?: string | null;
    createdAt?: string;
    note?: string;
};

type Mode = "vip" | "qr";

export default function ParkingAdminPage() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const webUrl = process.env.REACT_APP_WEB_URL;

    const [mode, setMode] = useState<Mode>("vip");

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<string | null>(null);

    const [search, setSearch] = useState("");
    const [persons, setPersons] = useState<PersonDto[]>([]);
    const [assignedPersons, setAssignedPersons] = useState<AssignedParkingDto[]>([]);

    const [qrDate, setQrDate] = useState<Date | null>(new Date());
    const [createdQrUrl, setCreatedQrUrl] = useState<string | null>(null);
    const [qrCodes, setQrCodes] = useState<ParkingQrListDto[]>([]);
    const [emailInputs, setEmailInputs] = useState<Record<number, string>>({});

    const [selectedQrScanUrl, setSelectedQrScanUrl] = useState<string | null>(null);
    const [selectedQrDisplayUrl, setSelectedQrDisplayUrl] = useState<string | null>(null);
    const [selectedQrInfo, setSelectedQrInfo] = useState<ParkingQrListDto | null>(null);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingPersons, setLoadingPersons] = useState(false);
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [loadingQrCodes, setLoadingQrCodes] = useState(false);

    const [addingPersonId, setAddingPersonId] = useState<number | null>(null);
    const [removingPersonId, setRemovingPersonId] = useState<number | null>(null);
    const [creatingQr, setCreatingQr] = useState(false);
    const [sendingQrId, setSendingQrId] = useState<number | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        loadEvents();
    }, []);

    const eventOptions = useMemo(() => {
        return Array.from(
            new Map(
                events
                    .map((e) => {
                        const id = e.eventID;
                        const label = e.eventName;

                        if (id === undefined || id === null) return null;

                        return {
                            value: String(id),
                            label: String(label ?? `Event ${id}`),
                        };
                    })
                    .filter(
                        (x): x is { value: string; label: string } => x !== null
                    )
                    .map((x) => [x.value, x])
            ).values()
        );
    }, [events]);

    const assignedPersonIds = useMemo(() => {
        return new Set(assignedPersons.map((p) => p.personId));
    }, [assignedPersons]);

    async function loadEvents() {
        if (!apiUrl) {
            setError("REACT_APP_API_URL is missing.");
            return;
        }

        setLoadingEvents(true);
        setError(null);

        try {
            const res = await fetch(`${apiUrl}/api/events/light`);

            if (!res.ok) {
                throw new Error("Could not load events.");
            }

            const data = await res.json();
            setEvents(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setEvents([]);
            setError(err?.message || "An error occurred while loading events.");
        } finally {
            setLoadingEvents(false);
        }
    }

    async function searchPersons() {
        if (!apiUrl) {
            setError("REACT_APP_API_URL is missing.");
            return;
        }

        if (!eventId) {
            setError("Please select an event first.");
            return;
        }

        setLoadingPersons(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${apiUrl}/api/persons?search=${encodeURIComponent(search)}&page=1&pageSize=50&eventId=${eventId}`, {
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
            })


            if (!res.ok) {
                throw new Error("Could not load persons.");
            }

            const data: PersonsResponse | PersonDto[] = await res.json();

            if (Array.isArray(data)) {
                setPersons(data);
            } else {
                setPersons(
                    Array.isArray(data?.items)
                        ? data.items
                        : Array.isArray(data?.Items)
                            ? data.Items
                            : []
                );
            }
        } catch (err: any) {
            setPersons([]);
            setError(err?.message || "An error occurred while searching.");
        } finally {
            setLoadingPersons(false);
        }
    }

    async function loadAssignedPersons(selectedEventId?: string | null) {
        const currentEventId = selectedEventId ?? eventId;

        if (!apiUrl || !currentEventId) {
            setAssignedPersons([]);
            return;
        }

        setLoadingAssigned(true);

        try {
            const res = await fetch(`${apiUrl}/api/parking/event/${currentEventId}`, {
                headers: getAuthHeaders(),
            })

            if (!res.ok) {
                throw new Error("Could not load assigned VIP parking.");
            }

            const data = await res.json();
            setAssignedPersons(Array.isArray(data) ? data : []);
        } catch {
            setAssignedPersons([]);
        } finally {
            setLoadingAssigned(false);
        }
    }

    async function loadQrCodes(selectedEventId?: string | null) {
        const currentEventId = selectedEventId ?? eventId;

        if (!apiUrl || !currentEventId) {
            setQrCodes([]);
            return;
        }

        setLoadingQrCodes(true);

        try {
            const res = await fetch(`${apiUrl}/api/parking/qr/event/${currentEventId}`, {
                headers: getAuthHeaders(),
            })

            if (!res.ok) {
                throw new Error("Could not load QR codes.");
            }

            const data = await res.json();
            setQrCodes(Array.isArray(data) ? data : []);
        } catch {
            setQrCodes([]);
        } finally {
            setLoadingQrCodes(false);
        }
    }

    async function addParking(personId: number) {
        if (!apiUrl) {
            setError("REACT_APP_API_URL is missing.");
            return;
        }

        if (!eventId) {
            setError("Please select an event first.");
            return;
        }

        setAddingPersonId(personId);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${apiUrl}/api/parking/add`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    eventId: Number(eventId),
                    personId
                })
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Could not add person to VIP parking.");
            }

            const addedPerson = persons.find((p) => p.personId === personId);

            setSuccessMessage(
                addedPerson
                    ? `${addedPerson.firstName} ${addedPerson.lastName} was added to VIP parking.`
                    : "Person was added to VIP parking."
            );

            await loadAssignedPersons(eventId);
        } catch (err: any) {
            setError(err?.message || "An error occurred while adding the person.");
        } finally {
            setAddingPersonId(null);
        }
    }

    async function removeParking(personId: number) {
        if (!apiUrl || !eventId) return;

        setRemovingPersonId(personId);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${apiUrl}/api/parking/${eventId}/${personId}`, {
                method: "DELETE"
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Could not remove person.");
            }

            setSuccessMessage("Person was removed from VIP parking.");
            await loadAssignedPersons(eventId);
        } catch (err: any) {
            setError(err?.message || "An error occurred while removing the person.");
        } finally {
            setRemovingPersonId(null);
        }
    }

    function formatDateOnly(date: Date) {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    async function createEventQr() {
        if (!apiUrl) {
            setError("REACT_APP_API_URL is missing.");
            return;
        }

        if (!webUrl) {
            setError("REACT_APP_WEB_URL is missing.");
            return;
        }

        if (!eventId) {
            setError("Please select an event first.");
            return;
        }

        if (!qrDate) {
            setError("Please select a valid date.");
            return;
        }

        setCreatingQr(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${apiUrl}/api/parking/qr/create`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),

                body: JSON.stringify({
                    eventId: Number(eventId),
                    validDate: formatDateOnly(qrDate)
                })
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Could not create QR code.");
            }

            const data: EventQrCreateResponse = await res.json();

            if (!data.token) {
                throw new Error("API did not return a token.");
            }

            const displayUrl = `${webUrl}/parking-pass/${data.token}`;
            const scanUrl = `${webUrl}/parking-scan/${data.token}`;

            const selected: ParkingQrListDto = {
                parkingQrCodeId: data.parkingQrCodeId ?? 0,
                eventId: Number(eventId),
                token: data.token,
                validDate: data.validDate ?? formatDateOnly(qrDate),
                isUsed: false,
                usedAt: null,
                createdAt: new Date().toISOString(),
            };

            setCreatedQrUrl(displayUrl);
            setSelectedQrDisplayUrl(displayUrl);
            setSelectedQrScanUrl(scanUrl);
            setSelectedQrInfo(selected);
            setSuccessMessage("One-time parking QR code was created.");

            await loadQrCodes(eventId);
        } catch (err: any) {
            setError(err?.message || "An error occurred while creating the QR code.");
        } finally {
            setCreatingQr(false);
        }
    }

    async function sendQrEmail(parkingQrCodeId: number) {
        if (!apiUrl) return;

        const email = emailInputs[parkingQrCodeId]?.trim();

        if (!email) {
            setError("Please enter an email address.");
            return;
        }

        setSendingQrId(parkingQrCodeId);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${apiUrl}/api/parking/qr/send-email`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    parkingQrCodeId,
                    email
                })
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Could not send email.");
            }

            setSuccessMessage("QR code email was sent.");
        } catch (err: any) {
            setError(err?.message || "An error occurred while sending email.");
        } finally {
            setSendingQrId(null);
        }
    }

    return (
        <Container size="md" py="md">
            <Stack gap="md">
                <Title order={2}>Parking Admin</Title>

                {error && (
                    <Alert color="red" variant="light">
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert color="green" variant="light">
                        {successMessage}
                    </Alert>
                )}

                <Paper withBorder p="md" radius="md">
                    <Stack gap="md">
                        <SegmentedControl
                            value={mode}
                            onChange={(value) => {
                                setMode(value as Mode);
                                setCreatedQrUrl(null);
                                setSelectedQrScanUrl(null);
                                setSelectedQrDisplayUrl(null);
                                setSelectedQrInfo(null);
                                setSuccessMessage(null);
                                setError(null);

                                if (value === "vip" && eventId) {
                                    loadAssignedPersons(eventId);
                                }

                                if (value === "qr" && eventId) {
                                    loadQrCodes(eventId);
                                }
                            }}
                            data={[
                                { label: "VIP Parking", value: "vip" },
                                { label: "One-time QR", value: "qr" }
                            ]}
                        />

                        {loadingEvents ? (
                            <Group>
                                <Loader size="sm" color="red" />
                                <Text>Loading events...</Text>
                            </Group>
                        ) : (
                            <Select
                                label="Event"
                                placeholder="Select event"
                                value={eventId}
                                onChange={(value) => {
                                    setEventId(value);
                                    setPersons([]);
                                    setSuccessMessage(null);
                                    setError(null);
                                    setCreatedQrUrl(null);
                                    setSelectedQrScanUrl(null);
                                    setSelectedQrDisplayUrl(null);
                                    setSelectedQrInfo(null);

                                    if (mode === "vip") {
                                        loadAssignedPersons(value);
                                        setQrCodes([]);
                                    } else {
                                        setAssignedPersons([]);
                                        loadQrCodes(value);
                                    }
                                }}
                                searchable
                                clearable
                                data={eventOptions}
                            />
                        )}

                        {mode === "vip" && (
                            <Group align="end">
                                <TextInput
                                    label="Search person"
                                    placeholder="Enter name..."
                                    value={search}
                                    onChange={(e) => setSearch(e.currentTarget.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            searchPersons();
                                        }
                                    }}
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    color="red"
                                    onClick={searchPersons}
                                    loading={loadingPersons}
                                    disabled={!eventId}
                                >
                                    Search
                                </Button>
                            </Group>
                        )}

                        {mode === "qr" && (
                            <>
                                <DateInput
                                    label="Valid date"
                                    placeholder="Select date"
                                    value={qrDate}
                                    onChange={setQrDate}
                                />

                                <Group justify="flex-end">
                                    <Button
                                        color="red"
                                        onClick={createEventQr}
                                        loading={creatingQr}
                                        disabled={!eventId || !qrDate}
                                    >
                                        Create QR
                                    </Button>
                                </Group>
                            </>
                        )}
                    </Stack>
                </Paper>

                {mode === "vip" && (
                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm">
                            <Text fw={600}>Persons</Text>

                            {loadingPersons ? (
                                <Group>
                                    <Loader size="sm" color="red" />
                                    <Text>Searching persons...</Text>
                                </Group>
                            ) : persons.length === 0 ? (
                                <Text c="dimmed">No persons found.</Text>
                            ) : (
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Name</Table.Th>
                                            <Table.Th>Person ID</Table.Th>
                                            <Table.Th></Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {persons.map((p) => {
                                            const alreadyAssigned = assignedPersonIds.has(p.personId);

                                            return (
                                                <Table.Tr key={p.personId}>
                                                    <Table.Td>
                                                        {p.firstName} {p.lastName}
                                                    </Table.Td>
                                                    <Table.Td>{p.personId}</Table.Td>
                                                    <Table.Td>
                                                        <Button
                                                            color="red"
                                                            size="xs"
                                                            disabled={alreadyAssigned}
                                                            loading={addingPersonId === p.personId}
                                                            onClick={() => addParking(p.personId)}
                                                        >
                                                            {alreadyAssigned ? "Already assigned" : "Add"}
                                                        </Button>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            )}
                        </Stack>
                    </Paper>
                )}

                {mode === "vip" && (
                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm">
                            <Text fw={600}>Assigned VIP parking</Text>

                            {!eventId ? (
                                <Text c="dimmed">Select an event to see assigned persons.</Text>
                            ) : loadingAssigned ? (
                                <Group>
                                    <Loader size="sm" color="red" />
                                    <Text>Loading assigned persons...</Text>
                                </Group>
                            ) : assignedPersons.length === 0 ? (
                                <Text c="dimmed">No persons have been assigned VIP parking yet.</Text>
                            ) : (
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Name</Table.Th>
                                            <Table.Th>Person ID</Table.Th>
                                            <Table.Th>Created</Table.Th>

                                            <Table.Th></Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {assignedPersons.map((p) => (
                                            <Table.Tr key={p.parkingPersonId}>
                                                <Table.Td>
                                                    {(p.firstName || "")} {(p.lastName || "")}
                                                </Table.Td>
                                                <Table.Td>{p.personId}</Table.Td>
                                                <Table.Td>
                                                    {p.createdAt
                                                        ? new Date(p.createdAt).toLocaleString("en-GB")
                                                        : "-"}
                                                </Table.Td>
                                                <Table.Td>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Button
                                                        color="red"
                                                        variant="light"
                                                        size="xs"
                                                        loading={removingPersonId === p.personId}
                                                        onClick={() => removeParking(p.personId)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            )}
                        </Stack>
                    </Paper>
                )}

                {mode === "qr" && createdQrUrl && (
                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm" align="center">
                            <Text fw={600}>Generated QR page</Text>

                            <Text size="sm" c="dimmed" ta="center">
                                Share this page with the guest. The QR code will be shown there without using it.
                            </Text>

                            <Text size="xs" c="dimmed" ta="center">
                                {createdQrUrl}
                            </Text>
                        </Stack>
                    </Paper>
                )}

                {mode === "qr" && (
                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm">
                            <Text fw={600}>Created QR codes</Text>

                            {!eventId ? (
                                <Text c="dimmed">Select an event to see created QR codes.</Text>
                            ) : loadingQrCodes ? (
                                <Group>
                                    <Loader size="sm" color="red" />
                                    <Text>Loading QR codes...</Text>
                                </Group>
                            ) : qrCodes.length === 0 ? (
                                <Text c="dimmed">No QR codes created yet.</Text>
                            ) : (
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Valid date</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                                        <Table.Th>Created</Table.Th>
                                                        <Table.Th>Sent to</Table.Th>
                                            <Table.Th>Email</Table.Th>
                                            <Table.Th>Actions</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {qrCodes.map((qr) => {
                                            const displayUrl = `${webUrl}/parking-pass/${qr.token}`;
                                            const scanUrl = `${webUrl}/parking-scan/${qr.token}`;

                                            return (
                                                <Table.Tr key={qr.parkingQrCodeId}>
                                                    <Table.Td>
                                                        {qr.validDate
                                                            ? new Date(qr.validDate).toLocaleDateString("en-GB")
                                                            : "-"}
                                                    </Table.Td>

                                                    <Table.Td>
                                                        {qr.isUsed ? (
                                                            <>
                                                                <Badge color="red" variant="light">
                                                                    Used
                                                                </Badge>
                                                                {qr.usedAt && (
                                                                    <Text size="xs" c="dimmed" mt={4}>
                                                                        {new Date(qr.usedAt).toLocaleString("en-GB")}
                                                                    </Text>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Badge color="green" variant="light">
                                                                Not used
                                                            </Badge>
                                                        )}
                                                    </Table.Td>

                                                    <Table.Td>
                                                        {qr.createdAt
                                                            ? new Date(qr.createdAt).toLocaleString("en-GB")
                                                            : "-"}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
                                                            {qr.note || "-"}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td style={{ minWidth: 220 }}>
                                                        <TextInput
                                                            placeholder="Enter email"
                                                            value={emailInputs[qr.parkingQrCodeId] ?? ""}
                                                            onChange={(e) =>
                                                                setEmailInputs((prev) => ({
                                                                    ...prev,
                                                                    [qr.parkingQrCodeId]: e.currentTarget.value
                                                                }))
                                                            }
                                                        />
                                                    </Table.Td>

                                                    <Table.Td>
                                                        <Stack gap="xs">
                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                color="blue"
                                                                onClick={() => {
                                                                    setSelectedQrDisplayUrl(displayUrl);
                                                                    setSelectedQrScanUrl(scanUrl);
                                                                    setSelectedQrInfo(qr);
                                                                }}
                                                            >
                                                                Show QR
                                                            </Button>

                                                            <Button
                                                                component="a"
                                                                href={displayUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                size="xs"
                                                                variant="light"
                                                                color="gray"
                                                            >
                                                                Open page
                                                            </Button>

                                                            <Button
                                                                size="xs"
                                                                color="red"
                                                                loading={sendingQrId === qr.parkingQrCodeId}
                                                                onClick={() => sendQrEmail(qr.parkingQrCodeId)}
                                                            >
                                                                Send email
                                                            </Button>
                                                        </Stack>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            )}
                        </Stack>
                    </Paper>
                )}

                {mode === "qr" && selectedQrScanUrl && selectedQrInfo && (
                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm" align="center">
                            <Text fw={600}>Selected QR code</Text>

                            <QRGenerator value={selectedQrScanUrl} />

                            <Text size="sm">
                                Valid date:{" "}
                                {selectedQrInfo.validDate
                                    ? new Date(selectedQrInfo.validDate).toLocaleDateString("en-GB")
                                    : "-"}
                            </Text>

                            <Text size="sm">
                                Status: {selectedQrInfo.isUsed ? "Used" : "Not used"}
                            </Text>

                            {selectedQrInfo.usedAt && (
                                <Text size="xs" c="dimmed">
                                    Used at: {new Date(selectedQrInfo.usedAt).toLocaleString("en-GB")}
                                </Text>
                            )}

                            {selectedQrDisplayUrl && (
                                <Text size="xs" c="dimmed" ta="center">
                                    Guest page: {selectedQrDisplayUrl}
                                </Text>
                            )}

                            <Text size="xs" c="dimmed" ta="center">
                                QR scans to: {selectedQrScanUrl}
                            </Text>

                            <Button
                                size="xs"
                                variant="light"
                                color="gray"
                                onClick={() => {
                                    setSelectedQrScanUrl(null);
                                    setSelectedQrDisplayUrl(null);
                                    setSelectedQrInfo(null);
                                }}
                            >
                                Close
                            </Button>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Container>
    );
}